import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { useLocation, useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { db, storage } from '../../../shared/storage/firebase';
import { useAuth } from '../../auth/contexts/AuthContext';
import {
  AvailabilityDayTemplate,
  AvailabilityMaybeReason,
  AvailabilityQuarterKey,
  AvailabilityStatus,
  AvailabilityWeekTemplate,
  TeacherAvailabilityQuarterTemplate,
  TeacherSchoolClearanceItem,
  UploadedFileMeta,
  User,
} from '../../../types';
import {
  cloneAvailability,
  currentAvailabilityQuarterKey,
  defaultAvailabilityWeek,
  firstAvailabilityBlock,
  isValidTime,
  minutes,
  normalizeAvailabilityTimeRange,
  planningAvailabilityQuarterKey,
  TEACHER_AVAILABILITY_DAYS,
  TEACHER_AVAILABILITY_MAYBE_REASONS,
  TEACHER_AVAILABILITY_QUARTERS,
  TeacherAvailabilityDayKey,
} from '../utils/teacherAvailability';
import '../styles/TeacherAdminDetailPage.css';
import { flattenLegacyClearanceItems, parsePartnerTeacherClearance } from '../../partners/utils/teacherClearances';
import { listCertifications, listLearningModules, listLearningTrainings, listTeacherLearningProgress, saveStepProgress, LearningProgressEntry } from '../../resources/services/learningCenterService';
import { fallbackCertifications } from '../../resources/fallbackCertifications';
import {
  Certification,
  COMPLETION_TYPE_LABELS,
  formatTrainingDuration,
  getQuizQuestions,
  LearningModule,
  LearningTraining,
  resolveCertificationPath,
} from '../../resources/types';
import { ADMIN_PERMISSIONS, normalizeAdminPermissions, type AdminPermission } from '../../auth/adminPermissions';

type Props = {
  teacherId: string;
};

type TeacherEvent = {
  id: string;
  title: string;
  squadId: string;
  role: 'lead' | 'support';
  start: Date;
  end: Date;
};

type LocationState = {
  from?: 'admin-teachers' | 'teacher-availability' | 'learning';
  backTo?: string;
  backState?: any;
};

type WeekDrafts = Partial<Record<AvailabilityQuarterKey, AvailabilityWeekTemplate>>;
type TeacherDetailTab = 'overview' | 'profile' | 'compliance' | 'availability';
type ExceptionDraft = {
  status: AvailabilityStatus;
  maybeReason: AvailabilityMaybeReason | null;
  maybeNote: string;
  start: string;
  end: string;
};

const DEFAULT_BACK_STATE = { state: { adminTab: 'users', adminUserTab: 'teachers' } };

function toDate(v: any): Date | null {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate();
  if (v instanceof Date) return v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDate(v: any, options?: Intl.DateTimeFormatOptions): string {
  const d = toDate(v);
  if (!d) return '-';
  return d.toLocaleDateString(undefined, options || { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(v: any): string {
  const d = toDate(v);
  if (!d) return '-';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function firstFileUrl(...candidates: unknown[]): string | null {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate) return candidate;
    const url = (candidate as any)?.url;
    if (typeof url === 'string' && url) return url;
    if (Array.isArray(candidate)) {
      const nested = firstFileUrl(...candidate);
      if (nested) return nested;
    }
  }
  return null;
}

type ComplianceItem = {
  id: string;
  label: string;
  done: boolean;
  group: string;
  action: 'toggleOnboarding' | 'upload' | 'toggle' | 'view';
  optional?: boolean;
  pending?: boolean;
  detail?: string | null;
  downloadUrl?: string | null;
  downloadLabel?: string;
};

type StateBackgroundCheckSubmission = {
  teacherId: string;
  checkType: string;
  jurisdiction: string;
  submittedAt?: unknown;
  form: Record<string, any>;
};

const CORI_FORM_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'firstName', label: 'First name' }, { key: 'middleInitial', label: 'Middle initial' },
  { key: 'lastName', label: 'Last name' }, { key: 'suffix', label: 'Suffix' },
  { key: 'formerLastNames', label: 'Former last names' }, { key: 'dateOfBirth', label: 'Date of birth' },
  { key: 'placeOfBirth', label: 'Place of birth' }, { key: 'lastSixSsn', label: 'Last six digits of SSN' },
  { key: 'sex', label: 'Sex' }, { key: 'height', label: 'Height' },
  { key: 'eyeColor', label: 'Eye color' }, { key: 'race', label: 'Race' },
  { key: 'governmentIdNumber', label: 'Driver’s license or ID number' }, { key: 'governmentIdState', label: 'State of issue' },
  { key: 'fatherFullName', label: 'Father’s full name' }, { key: 'motherFullName', label: 'Mother’s full name' },
  { key: 'streetAddress', label: 'Street address' }, { key: 'apartment', label: 'Apartment or suite' },
  { key: 'city', label: 'City' }, { key: 'state', label: 'State' }, { key: 'zip', label: 'ZIP' },
  { key: 'preferredEmail', label: 'Preferred email' }, { key: 'phone', label: 'Phone' },
  { key: 'signedName', label: 'Electronic signature' },
];

const teacherLocationContext = (teacher: User | null) =>
  String((teacher as any)?.hiring?.metro || (teacher as any)?.hiring?.locationLabel || (teacher as any)?.state || teacher?.shippingAddress?.state || '').toLowerCase();

const isMassachusettsLocation = (locationContext: string) =>
  locationContext.includes('massachusetts')
  || /\bma\b/.test(locationContext)
  || ['boston', 'wayland', 'brookline', 'cambridge', 'arlington', 'swampscott', 'sudbury', 'dover', 'framingham']
    .some(place => locationContext.includes(place));

const onboardingTaskId = (step: { id: string; onboardingTaskId?: string }) => step.onboardingTaskId || step.id;

const learningStatusLabel = (status?: string) =>
  status === 'completed' ? 'Complete' : status === 'pending_review' ? 'Awaiting review' : status === 'rejected' ? 'Needs resubmission' : 'Not started';

type CertificationPathRow =
  | { kind: 'module'; module: LearningModule; lessons: Array<{ training: LearningTraining; stepId: string; entry?: LearningProgressEntry; done: boolean; pending: boolean }>; complete: number; total: number; done: boolean }
  | { kind: 'training'; training: LearningTraining; stepId: string; entry?: LearningProgressEntry; done: boolean; pending: boolean }
  | { kind: 'step'; step: Certification['steps'][number]; entry?: LearningProgressEntry; done: boolean; pending: boolean };

function formatAddress(address: any): string {
  if (!address) return '-';
  const line1 = [address.line1, address.line2].filter(Boolean).join(' ');
  const cityState = [address.city, [address.state, address.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [line1, cityState, address.country].filter(Boolean).join(' | ') || address.formattedAddress || '-';
}

function formatEmergencyContact(contact: any): string {
  if (!contact) return '-';
  return [contact.name, formatUsPhoneDisplay(contact.phone), contact.relationship].filter(Boolean).join(' | ') || '-';
}

function formatUsPhoneDisplay(input: unknown): string {
  const raw = typeof input === 'string' ? input : String(input ?? '');
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  const hasUsCountryCode = digits.length >= 11 && digits.startsWith('1');
  const main = hasUsCountryCode ? digits.slice(1) : digits;
  if (main.length !== 10) return raw;
  const prefix = hasUsCountryCode ? '1 ' : '';
  return `${prefix}(${main.slice(0, 3)}) ${main.slice(3, 6)}-${main.slice(6)}`;
}

function normalizeUsPhoneForSave(input: string): string | null {
  const digits = String(input || '').replace(/\D/g, '');
  if (digits.length === 0) return '';
  if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
  if (digits.length === 10) return digits;
  return null;
}

function uploadedAt(meta?: UploadedFileMeta | null): string {
  return meta?.uploadedAt ? formatDate(meta.uploadedAt) : 'No upload date';
}

function statusLabel(v: AvailabilityStatus): string {
  if (v === 'available') return 'Available';
  if (v === 'maybe') return 'Maybe';
  return 'Unavailable';
}

function complianceDone(v: unknown): boolean {
  if (Array.isArray(v)) return v.length > 0;
  return Boolean(v);
}

function eventFromDoc(docSnap: any, role: TeacherEvent['role']): TeacherEvent | null {
  const data = docSnap.data() || {};
  const start = toDate(data.start);
  const end = toDate(data.end);
  if (!start || !end) return null;
  return {
    id: docSnap.id,
    title: data.title || data.squadTitle || 'Class',
    squadId: data.squadId || '',
    role,
    start,
    end,
  };
}

function quarterContainsDate(q: AvailabilityQuarterKey, dateKey: string): boolean {
  const quarter = TEACHER_AVAILABILITY_QUARTERS.find(x => x.key === q);
  if (!quarter) return false;
  return dateKey >= quarter.startDate && dateKey <= quarter.endDate;
}

function ymdFromDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function dayKeyFromDate(d: Date): TeacherAvailabilityDayKey {
  const keys: TeacherAvailabilityDayKey[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  return keys[d.getDay()];
}

const TeacherAdminDetailPage: React.FC<Props> = ({ teacherId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useAuth();
  const locationState = (location.state || {}) as LocationState;

  const [teacher, setTeacher] = useState<User | null>(null);
  const [schoolClearances, setSchoolClearances] = useState<TeacherSchoolClearanceItem[]>([]);
  const [events, setEvents] = useState<TeacherEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedQuarter, setSelectedQuarter] = useState<AvailabilityQuarterKey>(() => planningAvailabilityQuarterKey());
  const [drafts, setDrafts] = useState<WeekDrafts>({});
  const [dirty, setDirty] = useState<Record<string, boolean>>({});
  const [savingQuarter, setSavingQuarter] = useState<AvailabilityQuarterKey | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fieldSaving, setFieldSaving] = useState<string | null>(null);
  const [fieldMessage, setFieldMessage] = useState<string | null>(null);
  const [selectedExceptionDate, setSelectedExceptionDate] = useState<string | null>(null);
  const [exceptionDraft, setExceptionDraft] = useState<ExceptionDraft | null>(null);
  const [exceptionSaving, setExceptionSaving] = useState(false);
  const [exceptionError, setExceptionError] = useState<string | null>(null);
  const [, setUploadingDocKey] = useState<string | null>(null);
  const [uploadingHeadshot, setUploadingHeadshot] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [learningCertifications, setLearningCertifications] = useState<Certification[]>([]);
  const [learningModules, setLearningModules] = useState<LearningModule[]>([]);
  const [learningTrainings, setLearningTrainings] = useState<LearningTraining[]>([]);
  const [learningProgress, setLearningProgress] = useState<LearningProgressEntry[]>([]);
  const [activeTab, setActiveTab] = useState<TeacherDetailTab>('overview');
  const [savingOnboardingTask, setSavingOnboardingTask] = useState<string | null>(null);
  const [savingLearningStep, setSavingLearningStep] = useState('');
  const [backgroundSubmission, setBackgroundSubmission] = useState<StateBackgroundCheckSubmission | null>(null);
  const [backgroundSubmissionLoading, setBackgroundSubmissionLoading] = useState(false);
  const [backgroundSubmissionError, setBackgroundSubmissionError] = useState<string | null>(null);

  const openCoriSubmission = async () => {
    setBackgroundSubmissionLoading(true);
    setBackgroundSubmissionError(null);
    try {
      const callable = httpsCallable(getFunctions(), 'hiringAdminGetStateBackgroundCheckSubmission');
      const result = await callable({ teacherId, checkType: 'ma-cori' });
      setBackgroundSubmission(result.data as StateBackgroundCheckSubmission);
    } catch (e: any) {
      setBackgroundSubmissionError(e?.message || 'Could not load the submitted CORI form.');
    } finally {
      setBackgroundSubmissionLoading(false);
    }
  };

  const loadTeacher = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const userSnap = await getDoc(doc(db, 'users', teacherId));
      if (!userSnap.exists()) {
        setError('Teacher not found.');
        setTeacher(null);
        return;
      }

      const user = { id: userSnap.id, ...(userSnap.data() as any) } as User;
      if (user.role !== 'teacher') {
        setError('This user is not a teacher.');
        setTeacher(null);
        return;
      }

      const [complianceSnap, legacyClearancesSnap, partnersSnap, byMain, bySupport] = await Promise.all([
        getDoc(doc(db, 'teachers', teacherId)),
        getDocs(collection(db, 'teachers', teacherId, 'schoolClearances')),
        getDocs(collection(db, 'partners')),
        getDocs(query(collection(db, 'squadEvents'), where('mainTeacherId', '==', teacherId))),
        getDocs(query(collection(db, 'squadEvents'), where('supportingTeacherIds', 'array-contains', teacherId))),
      ]);

      const merged = {
        ...user,
        ...(complianceSnap.exists() ? complianceSnap.data() : {}),
      } as User;

      const canonicalSnapshots = await Promise.all(partnersSnap.docs.map(partner =>
        getDoc(doc(db, 'partners', partner.id, 'teacherClearances', teacherId))
      ));
      const canonicalItems = canonicalSnapshots
        .filter(snapshot => snapshot.exists())
        .flatMap(snapshot => {
          const parsed = parsePartnerTeacherClearance(snapshot.id, snapshot.data() as Record<string, unknown>);
          return parsed.requirementStatuses.map(status => ({
            id: `${parsed.partnerId}:${status.requirementId}`,
            type: status.requirementId,
            typeLabel: `${parsed.partnerId} — ${status.status}`,
            uploadedAt: status.completedAt,
            historical: false,
          } as unknown as TeacherSchoolClearanceItem));
        });
      const historicalItems = flattenLegacyClearanceItems(legacyClearancesSnap.docs.map(snapshot => ({
        id: snapshot.id,
        data: snapshot.data() as Record<string, unknown>,
      }))) as unknown as TeacherSchoolClearanceItem[];
      const nextClearances = [...canonicalItems, ...historicalItems]
        .sort((a, b) => (toDate((b as any).uploadedAt)?.getTime() || 0) - (toDate((a as any).uploadedAt)?.getTime() || 0));

      const eventMap = new Map<string, TeacherEvent>();
      byMain.docs.forEach(d => {
        const ev = eventFromDoc(d, 'lead');
        if (ev) eventMap.set(ev.id, ev);
      });
      bySupport.docs.forEach(d => {
        const ev = eventFromDoc(d, 'support');
        if (!ev) return;
        const existing = eventMap.get(ev.id);
        eventMap.set(ev.id, existing ? { ...existing, role: 'lead' } : ev);
      });

      const nextDrafts: WeekDrafts = {};
      TEACHER_AVAILABILITY_QUARTERS.forEach(q => {
        const existing = (merged.availabilityTemplates as any)?.[q.key]?.week;
        nextDrafts[q.key] = cloneAvailability(existing || defaultAvailabilityWeek());
      });

      setTeacher(merged);
      setSchoolClearances(nextClearances);
      setEvents(Array.from(eventMap.values()).sort((a, b) => a.start.getTime() - b.start.getTime()));
      setDrafts(nextDrafts);
      setDirty({});
    } catch (e: any) {
      console.error('[TeacherAdminDetailPage] Failed to load teacher', e);
      setError(e?.message || 'Failed to load teacher.');
    } finally {
      setLoading(false);
    }
  }, [teacherId]);

  useEffect(() => {
    void loadTeacher();
  }, [loadTeacher]);

  useEffect(() => {
    let active = true;
    Promise.all([
      listCertifications().catch(() => [] as Certification[]),
      listLearningModules().catch(() => [] as LearningModule[]),
      listLearningTrainings().catch(() => [] as LearningTraining[]),
      listTeacherLearningProgress(teacherId).catch(() => [] as LearningProgressEntry[]),
    ])
      .then(([certifications, modules, trainings, progress]) => {
        if (!active) return;
        setLearningCertifications(certifications.length ? certifications : fallbackCertifications);
        setLearningModules(modules);
        setLearningTrainings(trainings);
        setLearningProgress(progress);
      })
      .catch(error => console.error('[TeacherAdminDetailPage] Failed to load certifications', error));
    return () => { active = false; };
  }, [teacherId]);

  const backLabel = locationState.from === 'teacher-availability'
    ? 'Back to availability'
    : locationState.from === 'learning'
      ? 'Back to Certifications Management'
      : 'Back to teachers';

  const goBack = () => {
    if (window.history.length > 1 && locationState.from) {
      navigate(-1);
      return;
    }
    navigate(locationState.backTo || '/app/admin', locationState.backState ? { state: locationState.backState } : DEFAULT_BACK_STATE);
  };

  const complianceItems = useMemo((): ComplianceItem[] => {
    if (!teacher) return [];
    const hiring = (teacher as any).hiring || {};
    const onboarding = hiring.onboarding || {};
    const onboardingDone = (id: string) => onboarding?.[id]?.status === 'completed';
    const locationContext = teacherLocationContext(teacher);
    const isMassachusetts = isMassachusettsLocation(locationContext);
    const shirtSize = String(hiring.shirtSize || (teacher as any).shirtSize || '').trim();
    const contractUrl = firstFileUrl(hiring.contractSignedPdf, teacher.signedContracts, hiring.contractUnsignedPdf);
    const handbook = onboarding.handbook || {};
    const handbookUrl = firstFileUrl(handbook.signatureUrl);
    const mandatedUrl = firstFileUrl(teacher.mandatoryReportingCertificationFile);
    const checkrPending = !onboardingDone('backgroundCheck')
      && teacher.backgroundCheckCleared !== true
      && onboarding.backgroundCheck?.status === 'pending_review';
    return [
      {
        id: 'offer',
        label: 'Offer accepted',
        done: Boolean(hiring.offerAcceptedAt) || onboardingDone('offer'),
        group: 'Onboarding',
        action: 'toggleOnboarding',
        detail: hiring.offerAcceptedAt ? `Accepted ${formatDateTime(hiring.offerAcceptedAt)}` : null,
      },
      {
        id: 'contract',
        label: 'Employment contract signed',
        done: Boolean(hiring.contractSignedAt) || onboardingDone('contract') || complianceDone(teacher.signedContracts),
        group: 'Onboarding',
        action: 'toggleOnboarding',
        downloadUrl: contractUrl,
        downloadLabel: hiring.contractSignedPdf?.url || firstFileUrl(teacher.signedContracts) ? 'Download contract' : 'Download unsigned PDF',
      },
      { id: 'slack', label: 'Slack account set up', done: onboardingDone('slack'), group: 'Onboarding', action: 'toggleOnboarding' },
      { id: 'profile', label: 'Profile and availability set up', done: onboardingDone('profile'), group: 'Onboarding', action: 'toggleOnboarding' },
      {
        id: 'handbook',
        label: 'Staff handbook signed',
        done: onboardingDone('handbook'),
        group: 'Onboarding',
        action: 'toggleOnboarding',
        detail: handbook.signedName ? `Signed by ${handbook.signedName}` : handbook.signatureMode === 'draw' ? 'Drawn signature on file' : null,
        downloadUrl: handbookUrl,
        downloadLabel: 'View signature',
      },
      { id: 'payroll', label: 'Payroll account set up', done: onboardingDone('payroll'), group: 'Onboarding', action: 'toggleOnboarding' },
      {
        id: 'merch',
        label: 'Shirt size provided',
        done: onboardingDone('merch'),
        group: 'Onboarding',
        action: 'toggleOnboarding',
        detail: shirtSize ? `Size: ${shirtSize}` : onboardingDone('merch') ? 'Size not recorded' : null,
      },
      {
        id: 'backgroundCheck',
        label: 'Checkr background check',
        done: onboardingDone('backgroundCheck') || teacher.backgroundCheckCleared === true,
        action: 'toggleOnboarding',
        group: 'Background checks',
        pending: checkrPending,
        detail: checkrPending
          ? (onboarding.backgroundCheck?.requestedAt ? `Requested ${formatDateTime(onboarding.backgroundCheck.requestedAt)}` : 'Teacher requested review')
          : null,
      },
      ...(isMassachusetts ? [{ id: 'schoolBackgroundCheck', label: 'Massachusetts CORI background check', done: onboardingDone('schoolBackgroundCheck') || (teacher as any).coriCleared === true, action: 'toggleOnboarding' as const, group: 'Background checks' }] : []),
      {
        id: 'mandatoryReportingCertificationFile',
        label: 'Mandatory reporter certification',
        done: onboardingDone('mandatedReporter') || complianceDone(teacher.mandatoryReportingCertificationFile),
        action: 'upload',
        group: 'Compliance',
        downloadUrl: mandatedUrl,
        downloadLabel: 'Download certificate',
      },
      { id: 'cycCleared', label: 'CYC clearance', done: complianceDone(teacher.cycCleared), action: 'toggle', group: 'Compliance' },
      { id: 'partnerClearances', label: 'Partner clearance records', done: schoolClearances.some(item => !(item as any).historical), action: 'view', group: 'Partner requirements', optional: true },
    ];
  }, [teacher, schoolClearances]);

  const requiredComplianceItems = complianceItems.filter(item => !item.optional);
  const complianceComplete = requiredComplianceItems.filter(i => i.done).length;
  const onboardingCertification = learningCertifications.find(item => item.kind === 'onboarding') || fallbackCertifications.find(item => item.kind === 'onboarding') || null;
  const trainingCertifications = learningCertifications.filter(certification => certification.kind !== 'onboarding');
  const onboardingPathSteps = [...(onboardingCertification?.steps || [])]
    .filter(step => step.published !== false)
    .sort((a, b) => a.order - b.order)
    .filter(step => {
      const taskId = onboardingTaskId(step);
      if (taskId !== 'schoolBackgroundCheck') return true;
      const cori = complianceItems.find(item => item.id === 'schoolBackgroundCheck');
      return Boolean(cori) || cori?.done;
    });
  const extraComplianceItems = complianceItems.filter(item =>
    item.id === 'cycCleared'
  );
  const onboardingItemFor = (taskId: string) =>
    complianceItems.find(item => item.id === (taskId === 'mandatedReporter' ? 'mandatoryReportingCertificationFile' : taskId));
  const onboardingCompleteCount = onboardingPathSteps.filter(step => onboardingItemFor(onboardingTaskId(step))?.done).length;
  const certificationViews = trainingCertifications.map(certification => {
    const progressByStep = new Map(learningProgress.filter(item => item.certificationId === certification.id).map(item => [item.stepId, item]));
    const rows = resolveCertificationPath(certification).map((item): CertificationPathRow | null => {
      if (item.kind === 'module') {
        const module = learningModules.find(entry => entry.id === item.id);
        if (!module) return null;
        const lessons = module.trainingIds
          .map(id => learningTrainings.find(entry => entry.id === id))
          .filter((training): training is LearningTraining => Boolean(training))
          .map(training => {
          const stepId = `training:${training.id}`;
          const entry = progressByStep.get(stepId);
          return { training, stepId, entry, done: entry?.status === 'completed', pending: entry?.status === 'pending_review' };
        });
        const complete = lessons.filter(lesson => lesson.done).length;
        return { kind: 'module' as const, module, lessons, complete, total: lessons.length, done: lessons.length > 0 && complete === lessons.length };
      }
      if (item.kind === 'training') {
        const training = learningTrainings.find(entry => entry.id === item.id);
        if (!training) return null;
        const stepId = `training:${training.id}`;
        const entry = progressByStep.get(stepId);
        return { kind: 'training' as const, training, stepId, entry, done: entry?.status === 'completed', pending: entry?.status === 'pending_review' };
      }
      const step = certification.steps.find(entry => entry.id === item.id);
      if (!step) return null;
      const entry = progressByStep.get(step.id);
      return { kind: 'step' as const, step, entry, done: entry?.status === 'completed', pending: entry?.status === 'pending_review' };
    }).filter((row): row is CertificationPathRow => row != null);
    const complete = rows.filter(row => row.done).length;
    return { certification, rows, complete, total: rows.length };
  });
  const currentQuarter = currentAvailabilityQuarterKey();
  const hasCurrentAvailability = Boolean((teacher?.availabilityTemplates as any)?.[currentQuarter]?.week);
  const upcomingEvents = events.filter(e => e.end.getTime() >= Date.now()).slice(0, 6);
  const currentQuarterEvents = events.filter(e => {
    const q = TEACHER_AVAILABILITY_QUARTERS.find(x => x.key === selectedQuarter);
    if (!q) return false;
    const key = e.start.toISOString().slice(0, 10);
    return key >= q.startDate && key <= q.endDate;
  });

  const selectedWeek = drafts[selectedQuarter] || defaultAvailabilityWeek();
  const quarterExceptions = useMemo(() => {
    const raw = ((teacher as any)?.availabilityExceptions || {})?.[selectedQuarter] || {};
    return Object.entries(raw)
      .filter(([dateKey]) => quarterContainsDate(selectedQuarter, dateKey))
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([dateKey, value]) => ({ dateKey, value: value as any }));
  }, [teacher, selectedQuarter]);

  const saveUserPatch = async (patch: Partial<User> & Record<string, any>, label: string) => {
    if (!teacher) return;
    try {
      setFieldSaving(label);
      setFieldMessage(null);
      await updateDoc(doc(db, 'users', teacher.id), patch as any);
      setTeacher(prev => prev ? ({ ...prev, ...patch } as User) : prev);
      setFieldMessage(`Saved ${label}.`);
    } catch (e: any) {
      console.error(`[TeacherAdminDetailPage] Failed to save ${label}`, e);
      setFieldMessage(e?.message || `Failed to save ${label}.`);
    } finally {
      setFieldSaving(null);
    }
  };

  const toggleAdminPermission = async (permission: AdminPermission) => {
    if (!teacher || teacher.isAdmin) return;
    const current = normalizeAdminPermissions(teacher.adminPermissions);
    const next = current.includes(permission) ? current.filter(item => item !== permission) : [...current, permission];
    await saveUserPatch({ adminPermissions: next }, 'admin permissions');
  };

  const saveCompliancePatch = async (patch: Partial<User> & Record<string, any>, label: string) => {
    if (!teacher) return;
    try {
      setFieldSaving(label);
      setFieldMessage(null);
      await setDoc(doc(db, 'teachers', teacher.id), patch as any, { merge: true });
      setTeacher(prev => prev ? ({ ...prev, ...patch } as User) : prev);
      setFieldMessage(`Saved ${label}.`);
    } catch (e: any) {
      console.error(`[TeacherAdminDetailPage] Failed to save ${label}`, e);
      setFieldMessage(e?.message || `Failed to save ${label}.`);
    } finally {
      setFieldSaving(null);
    }
  };

  const activateIfOnboardingComplete = async (candidate: User) => {
    if (candidate.staffStatus === 'offboarded') return;
    const hiring = (candidate as any).hiring || {};
    const onboarding = hiring.onboarding || {};
    const configured = learningCertifications.find(item => item.kind === 'onboarding');
    const requiredIds = configured?.steps
      .filter(step => step.published !== false && step.required !== false)
      .map(step => step.onboardingTaskId || step.id)
      || ['offer', 'contract', 'slack', 'profile', 'handbook', 'backgroundCheck', 'mandatedReporter', 'payroll', 'merch'];
    const locationContext = teacherLocationContext(candidate);
    const isMassachusetts = isMassachusettsLocation(locationContext);
    if (isMassachusetts && !requiredIds.includes('schoolBackgroundCheck')) {
      requiredIds.push('schoolBackgroundCheck');
    }
    const complete = (id: string) => {
      if (id === 'offer') return Boolean(hiring.offerAcceptedAt) || onboarding.offer?.status === 'completed';
      if (id === 'contract') return Boolean(hiring.contractSignedAt) || onboarding.contract?.status === 'completed' || complianceDone(candidate.signedContracts);
      if (id === 'backgroundCheck') {
        return onboarding.backgroundCheck?.status === 'completed'
          || onboarding.backgroundCheck?.status === 'pending_review'
          || candidate.backgroundCheckCleared === true;
      }
      if (id === 'schoolBackgroundCheck') return onboarding.schoolBackgroundCheck?.status === 'completed' || (candidate as any).coriCleared === true;
      if (id === 'mandatedReporter') return onboarding.mandatedReporter?.status === 'completed' || complianceDone(candidate.mandatoryReportingCertificationFile);
      return onboarding[id]?.status === 'completed';
    };
    const onboardingInProgress = ['offer_sent', 'offer_accepted', 'onboarding']
      .includes(String(hiring.status || (candidate as any).hiringStatus || ''));
    if (requiredIds.length > 0
      && requiredIds.every(complete)
      && (candidate.staffStatus !== 'active' || onboardingInProgress)) {
      await Promise.all([
        updateDoc(doc(db, 'users', candidate.id), {
          staffStatus: 'active',
          hiringStatus: 'onboarding_complete',
          onboardingCompletedAt: Timestamp.now(),
        }),
        updateDoc(doc(db, 'teachers', candidate.id), {
          'hiring.status': 'onboarding_complete',
          'hiring.onboardingCompletedAt': Timestamp.now(),
        }),
      ]);
      setTeacher(previous => previous ? ({ ...previous, staffStatus: 'active' } as User) : previous);
    }
  };

  const toggleOnboardingTask = async (taskId: string, currentlyDone: boolean) => {
    if (!teacher || savingOnboardingTask) return;
    const nextStatus = currentlyDone ? 'not_started' : 'completed';
    try {
      setSavingOnboardingTask(taskId);
      setFieldMessage(null);
      const userPatch = taskId === 'backgroundCheck'
        ? { backgroundCheckCleared: !currentlyDone }
        : taskId === 'schoolBackgroundCheck'
          ? { coriCleared: !currentlyDone }
          : null;
      await Promise.all([
        updateDoc(doc(db, 'teachers', teacher.id), {
          [`hiring.onboarding.${taskId}.status`]: nextStatus,
          [`hiring.onboarding.${taskId}.completedAt`]: currentlyDone ? deleteField() : Timestamp.now(),
        }),
        ...(userPatch ? [updateDoc(doc(db, 'users', teacher.id), userPatch)] : []),
      ]);
      const hiring = { ...((teacher as any).hiring || {}) };
      const onboarding = { ...(hiring.onboarding || {}) };
      onboarding[taskId] = {
        ...(onboarding[taskId] || {}),
        status: nextStatus,
        completedAt: currentlyDone ? null : Timestamp.now(),
      };
      const nextTeacher = { ...teacher, ...userPatch, hiring: { ...hiring, onboarding } } as unknown as User;
      setTeacher(previous => {
        if (!previous) return previous;
        return { ...previous, ...userPatch, hiring: (nextTeacher as any).hiring } as unknown as User;
      });
      if (!currentlyDone) await activateIfOnboardingComplete(nextTeacher);
      setFieldMessage(`${currentlyDone ? 'Unchecked' : 'Completed'} onboarding item.`);
    } catch (e: any) {
      console.error('[TeacherAdminDetailPage] Failed to update onboarding item', e);
      setFieldMessage(e?.message || 'Failed to update onboarding item.');
    } finally {
      setSavingOnboardingTask(null);
    }
  };

  const saveLearningStep = async (certificationId: string, stepId: string, status: 'completed' | 'not_started' | 'pending_review' | 'rejected') => {
    const key = `${certificationId}_${stepId}`;
    if (savingLearningStep) return;
    const previous = learningProgress.find(item => item.id === key);
    setSavingLearningStep(key);
    setFieldMessage(null);
    setLearningProgress(current => {
      const next = current.filter(item => item.id !== key);
      if (status === 'not_started') return next;
      return [...next, {
        id: key,
        teacherId,
        certificationId,
        stepId,
        status,
        evidence: previous?.evidence,
        quizAnswer: previous?.quizAnswer,
        quizAnswers: previous?.quizAnswers,
      }];
    });
    try {
      await saveStepProgress(teacherId, certificationId, stepId, {
        status,
        completedAt: status === 'completed' ? new Date().toISOString() : null,
      });
      setFieldMessage(status === 'completed' ? 'Marked the teacher step complete.' : status === 'not_started' ? 'Reopened the teacher step.' : `Updated step to ${learningStatusLabel(status).toLowerCase()}.`);
    } catch (error: any) {
      setLearningProgress(current => {
        const next = current.filter(item => item.id !== key);
        return previous ? [...next, previous] : next;
      });
      setFieldMessage(error?.message || 'Could not update certification progress.');
    } finally {
      setSavingLearningStep('');
    }
  };

  const uploadComplianceFile = async (
    kind: 'overtureBgCheckFile' | 'mandatoryReportingCertificationFile' | 'signedContract',
    file: File
  ) => {
    if (!teacher || !file) return;

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isImage = file.type.startsWith('image/');
    if (!isPdf && !isImage) {
      setFieldMessage('Please upload a PDF or image file.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setFieldMessage('File too large. Maximum size is 10MB.');
      return;
    }

    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    const rand = Math.random().toString(16).slice(2, 8);
    const ext = (file.name.split('.').pop() || (isPdf ? 'pdf' : 'png')).toLowerCase().replace(/[^a-z0-9]/g, '');
    const folder =
      kind === 'overtureBgCheckFile'
        ? `teachers/${teacher.id}/compliance/overture-bg-check`
        : kind === 'mandatoryReportingCertificationFile'
          ? `teachers/${teacher.id}/compliance/mandatory-reporting`
          : `teachers/${teacher.id}/contracts`;
    const storagePath = `${folder}/${stamp}-${rand}.${ext || (isPdf ? 'pdf' : 'png')}`;
    const key = `${teacher.id}:${kind}:${storagePath}`;

    try {
      setUploadingDocKey(key);
      setFieldMessage(null);
      const { uploadBytes, getDownloadURL, ref: storageRef } = await import('firebase/storage');
      const r = storageRef(storage, storagePath);
      await uploadBytes(r, file, file.type ? { contentType: file.type } : undefined);
      const url = await getDownloadURL(r);
      const meta: UploadedFileMeta = {
        storagePath,
        url,
        filename: file.name || undefined,
        contentType: file.type || undefined,
        sizeBytes: file.size || undefined,
        uploadedAt: Timestamp.now(),
        uploadedBy: userData?.id || '',
      };

      if (kind === 'signedContract') {
        const prev = Array.isArray(teacher.signedContracts) ? teacher.signedContracts : [];
        await saveCompliancePatch({ signedContracts: [meta, ...prev] }, 'signed contract');
        await activateIfOnboardingComplete({ ...teacher, signedContracts: [meta, ...prev] });
      } else {
        await saveCompliancePatch({ [kind]: meta }, kind);
        await activateIfOnboardingComplete({ ...teacher, [kind]: meta });
      }
      setFieldMessage('Uploaded file.');
    } catch (e: any) {
      console.error('[TeacherAdminDetailPage] Failed to upload compliance file', e);
      setFieldMessage(e?.message || 'Upload failed.');
    } finally {
      setUploadingDocKey(null);
    }
  };

  const uploadHeadshot = async (file: File) => {
    if (!teacher || !file) return;
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setFieldMessage('Invalid image type. Upload JPG, PNG, GIF, or WebP.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setFieldMessage('Headshot too large. Maximum size is 5MB.');
      return;
    }

    try {
      setUploadingHeadshot(true);
      setFieldMessage(null);
      const { ref: storageRef, uploadBytes, getDownloadURL } = await import('firebase/storage');
      const ext = file.name.split('.').pop()?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
      const storagePath = `teachers/${teacher.id}/headshot.${ext}`;
      const r = storageRef(storage, storagePath);
      await uploadBytes(r, file, file.type ? { contentType: file.type } : undefined);
      const downloadUrl = await getDownloadURL(r);
      await updateDoc(doc(db, 'users', teacher.id), {
        headshotUrl: downloadUrl,
        headshotUpdatedAt: Timestamp.now(),
      });
      setTeacher(prev => prev ? ({ ...prev, headshotUrl: downloadUrl, headshotUpdatedAt: new Date() } as User) : prev);
      setFieldMessage('Uploaded profile picture.');
    } catch (e: any) {
      console.error('[TeacherAdminDetailPage] Failed to upload headshot', e);
      setFieldMessage(e?.message || 'Failed to upload profile picture.');
    } finally {
      setUploadingHeadshot(false);
    }
  };

  const removeHeadshot = async () => {
    if (!teacher?.headshotUrl || uploadingHeadshot) return;
    if (!await window.appConfirm(`Remove ${teacher.displayName || 'this teacher'}'s profile photo?`)) return;
    try {
      setUploadingHeadshot(true);
      setFieldMessage(null);
      await httpsCallable(getFunctions(), 'removeTeacherHeadshot')({ teacherId: teacher.id });
      setTeacher(previous => previous ? ({ ...previous, headshotUrl: undefined, headshotUpdatedAt: undefined } as User) : previous);
      setFieldMessage('Removed profile picture.');
    } catch (e: any) {
      console.error('[TeacherAdminDetailPage] Failed to remove headshot', e);
      setFieldMessage(e?.message || 'Failed to remove profile picture.');
    } finally {
      setUploadingHeadshot(false);
    }
  };

  const statusForDateKey = (dateKey: string): AvailabilityStatus | null => {
    if (!teacher) return null;
    const exception = ((teacher as any).availabilityExceptions || {})?.[selectedQuarter]?.[dateKey];
    if (exception?.status) return exception.status;
    if (exception?.type === 'unavailable') return 'unavailable';
    const date = new Date(`${dateKey}T12:00:00`);
    const dayKey = dayKeyFromDate(date);
    const day = selectedWeek?.[dayKey];
    return day?.status || null;
  };

  const openExceptionEditor = (dateKey: string) => {
    if (!teacher) return;
    const existing = ((teacher as any).availabilityExceptions || {})?.[selectedQuarter]?.[dateKey] || null;
    const dayKey = dayKeyFromDate(new Date(`${dateKey}T12:00:00`));
    const weeklyDay = selectedWeek?.[dayKey];
    const weeklyBlock = firstAvailabilityBlock(weeklyDay, dayKey);
    setSelectedExceptionDate(dateKey);
    setExceptionDraft({
      status: existing?.status || (existing?.type === 'unavailable' ? 'unavailable' : weeklyDay?.status || weeklyBlock.status || 'unavailable'),
      maybeReason: existing?.maybeReason || weeklyDay?.maybeReason || null,
      maybeNote: existing?.maybeNote || existing?.note || weeklyDay?.maybeNote || '',
      start: existing?.start || weeklyBlock.start,
      end: existing?.end || weeklyBlock.end,
    });
    setExceptionError(null);
  };

  const closeExceptionEditor = () => {
    setSelectedExceptionDate(null);
    setExceptionDraft(null);
    setExceptionError(null);
  };

  const saveException = async () => {
    if (!teacher || !selectedExceptionDate || !exceptionDraft) return;
    const normalizedRange = normalizeAvailabilityTimeRange(exceptionDraft.start, exceptionDraft.end);
    if (!isValidTime(normalizedRange.start) || !isValidTime(normalizedRange.end)) {
      setExceptionError('Invalid time.');
      return;
    }
    if (minutes(normalizedRange.start) >= minutes(normalizedRange.end)) {
      setExceptionError('Start must be before end.');
      return;
    }
    if (exceptionDraft.status === 'maybe' && !exceptionDraft.maybeReason) {
      setExceptionError('Maybe requires a reason.');
      return;
    }
    const payload = {
      status: exceptionDraft.status,
      maybeReason: exceptionDraft.status === 'maybe' ? exceptionDraft.maybeReason : null,
      maybeNote: exceptionDraft.status === 'maybe' ? exceptionDraft.maybeNote : '',
      start: normalizedRange.start,
      end: normalizedRange.end,
      type: exceptionDraft.status === 'unavailable' ? 'unavailable' : undefined,
      timeMode: 'custom',
      note: exceptionDraft.status === 'maybe' ? exceptionDraft.maybeNote : '',
      updatedAt: new Date().toISOString(),
    };
    if (payload.type === undefined) delete (payload as any).type;

    try {
      setExceptionSaving(true);
      setExceptionError(null);
      await updateDoc(doc(db, 'users', teacher.id), {
        [`availabilityExceptions.${selectedQuarter}.${selectedExceptionDate}`]: payload,
      } as any);
      setTeacher(prev => {
        if (!prev) return prev;
        const next: any = { ...prev };
        next.availabilityExceptions = {
          ...(next.availabilityExceptions || {}),
          [selectedQuarter]: {
            ...((next.availabilityExceptions || {}) as any)[selectedQuarter],
            [selectedExceptionDate]: payload,
          },
        };
        return next;
      });
      closeExceptionEditor();
    } catch (e: any) {
      console.error('[TeacherAdminDetailPage] Failed to save exception', e);
      setExceptionError(e?.message || 'Failed to save exception.');
    } finally {
      setExceptionSaving(false);
    }
  };

  const deleteException = async () => {
    if (!teacher || !selectedExceptionDate) return;
    try {
      setExceptionSaving(true);
      setExceptionError(null);
      await updateDoc(doc(db, 'users', teacher.id), {
        [`availabilityExceptions.${selectedQuarter}.${selectedExceptionDate}`]: deleteField(),
      } as any);
      setTeacher(prev => {
        if (!prev) return prev;
        const next: any = { ...prev };
        const quarterMap = { ...(((next.availabilityExceptions || {}) as any)[selectedQuarter] || {}) };
        delete quarterMap[selectedExceptionDate];
        next.availabilityExceptions = {
          ...(next.availabilityExceptions || {}),
          [selectedQuarter]: quarterMap,
        };
        return next;
      });
      closeExceptionEditor();
    } catch (e: any) {
      console.error('[TeacherAdminDetailPage] Failed to delete exception', e);
      setExceptionError(e?.message || 'Failed to delete exception.');
    } finally {
      setExceptionSaving(false);
    }
  };

  const updateDraftDay = (dayKey: TeacherAvailabilityDayKey, patch: Partial<AvailabilityDayTemplate> & { start?: string; end?: string }) => {
    setDrafts(prev => {
      const week = cloneAvailability(prev[selectedQuarter] || defaultAvailabilityWeek());
      const current = (week as any)[dayKey] || {};
      const block = firstAvailabilityBlock(current, dayKey);
      const nextStatus = patch.status || current.status || block.status || 'unavailable';
      const nextStart = patch.start ?? block.start;
      const nextEnd = patch.end ?? block.end;
      (week as any)[dayKey] = {
        ...current,
        ...patch,
        status: nextStatus,
        maybeReason: nextStatus === 'maybe' ? (patch.maybeReason ?? current.maybeReason ?? null) : null,
        maybeNote: nextStatus === 'maybe' ? (patch.maybeNote ?? current.maybeNote ?? '') : '',
        timeDetailsEnabled: true,
        blocks: [{ start: nextStart, end: nextEnd, status: nextStatus }],
      };
      return { ...prev, [selectedQuarter]: week };
    });
    setDirty(prev => ({ ...prev, [selectedQuarter]: true }));
  };

  const validateWeek = (week: AvailabilityWeekTemplate): string | null => {
    for (const day of TEACHER_AVAILABILITY_DAYS) {
      const dayTemplate = (week as any)[day.key] as AvailabilityDayTemplate;
      const block = firstAvailabilityBlock(dayTemplate, day.key);
      if (!isValidTime(block.start) || !isValidTime(block.end)) return `${day.label}: invalid time.`;
      if (minutes(block.start) >= minutes(block.end)) return `${day.label}: start must be before end.`;
      if (dayTemplate.status === 'maybe' && !dayTemplate.maybeReason) return `${day.label}: maybe requires a reason.`;
    }
    return null;
  };

  const saveSelectedQuarter = async () => {
    if (!teacher) return;
    const week = drafts[selectedQuarter] || defaultAvailabilityWeek();
    const validationError = validateWeek(week);
    if (validationError) {
      setSaveError(validationError);
      return;
    }

    const payload: TeacherAvailabilityQuarterTemplate = {
      quarterKey: selectedQuarter,
      week,
      updatedAt: new Date().toISOString(),
    };

    try {
      setSavingQuarter(selectedQuarter);
      setSaveError(null);
      await updateDoc(doc(db, 'users', teacher.id), {
        [`availabilityTemplates.${selectedQuarter}`]: payload,
      } as any);
      setTeacher(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          availabilityTemplates: {
            ...prev.availabilityTemplates,
            [selectedQuarter]: payload,
          },
        };
      });
      setDirty(prev => ({ ...prev, [selectedQuarter]: false }));
    } catch (e: any) {
      console.error('[TeacherAdminDetailPage] Failed to save availability', e);
      setSaveError(e?.message || 'Failed to save availability.');
    } finally {
      setSavingQuarter(null);
    }
  };

  if (loading) {
    return (
      <div className="teacher-detail-root">
        <button type="button" className="teacher-detail-back" onClick={goBack}>{backLabel}</button>
        <div className="teacher-detail-loading">Loading teacher profile...</div>
      </div>
    );
  }

  if (error || !teacher) {
    return (
      <div className="teacher-detail-root">
        <button type="button" className="teacher-detail-back" onClick={goBack}>{backLabel}</button>
        <div className="teacher-detail-error">{error || 'Teacher not found.'}</div>
      </div>
    );
  }

  const initials = String(teacher.displayName || teacher.email || 'T').split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase();

  const openTab = (tab: TeacherDetailTab, targetId?: string) => {
    setActiveTab(tab);
    if (targetId) window.setTimeout(() => document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  };

  return (
    <div className="teacher-detail-root">
      <div className="teacher-detail-topbar">
        <button type="button" className="teacher-detail-back" onClick={goBack}>{backLabel}</button>
        <button type="button" className="teacher-detail-refresh" onClick={() => void loadTeacher()}>Refresh</button>
      </div>

      <section className="teacher-detail-hero" id="teacher-overview">
        <div className="teacher-detail-avatar-wrap">
          <div className={`teacher-detail-avatar-upload ${uploadingHeadshot ? 'is-uploading' : ''}`}>
            {teacher.headshotUrl && !avatarFailed ? (
              <img className="teacher-detail-avatar" src={teacher.headshotUrl} alt="" onError={() => setAvatarFailed(true)} />
            ) : (
              <div className="teacher-detail-avatar teacher-detail-avatar--fallback">
                <span className="teacher-detail-avatar-initials">{initials}</span>
              </div>
            )}
            <div className="teacher-detail-avatar-actions">
              <label className="teacher-detail-avatar-action">
                {uploadingHeadshot ? 'Working...' : teacher.headshotUrl ? 'Change photo' : 'Upload photo'}
                <input type="file" accept="image/jpeg,image/jpg,image/png,image/gif,image/webp" disabled={uploadingHeadshot} onChange={(e) => { const file = e.target.files?.[0]; if (file) void uploadHeadshot(file); e.target.value = ''; }} />
              </label>
              {teacher.headshotUrl && <button type="button" className="teacher-detail-avatar-remove" disabled={uploadingHeadshot} onClick={() => void removeHeadshot()}>Remove photo</button>}
            </div>
          </div>
        </div>
        <div className="teacher-detail-hero-main">
          <div className="teacher-detail-kicker">Teacher admin profile</div>
          <h1>{teacher.displayName || teacher.email || teacher.id}</h1>
          <button type="button" className="teacher-detail-summary-link" onClick={() => openTab('profile')}>{teacher.email || 'No email'} · {formatUsPhoneDisplay(teacher.phoneNumber) || 'No phone'} · {(teacher as any).state || teacher.shippingAddress?.state || teacher.neighborhood || 'No location'}</button>
          <button type="button" className="teacher-detail-summary-link" onClick={() => openTab('profile')}>{teacher.staffStatus || 'active'} · {teacher.teacherType || 'Teacher'} · {teacher.isPublic ? 'Public profile' : 'Private profile'}{teacher.isAdmin ? ' · Admin' : ''}</button>
        </div>
        <div className="teacher-detail-scorecard">
          <div className="teacher-detail-score">
            <span>{complianceComplete}/{requiredComplianceItems.length}</span>
            <small>Onboarding &amp; compliance complete</small>
          </div>
          <div className={hasCurrentAvailability ? 'teacher-detail-ok' : 'teacher-detail-warn'}>
            {hasCurrentAvailability ? 'Current season availability is set' : 'Current season availability is missing'}
          </div>
        </div>
      </section>

      <nav className="teacher-detail-section-nav" aria-label="Teacher profile sections">
        {(['overview', 'profile', 'compliance', 'availability'] as TeacherDetailTab[]).map(tab => (
          <button key={tab} type="button" className={activeTab === tab ? 'is-active' : ''} onClick={() => openTab(tab)}>
            {tab === 'availability' ? 'Availability & Classes' : tab[0].toUpperCase() + tab.slice(1)}
            {tab === 'compliance' && requiredComplianceItems.length - complianceComplete > 0 && <span className="teacher-detail-tab-badge">{requiredComplianceItems.length - complianceComplete}</span>}
          </button>
        ))}
      </nav>

      {activeTab === 'overview' && <section className="teacher-detail-priority-grid teacher-detail-tab-panel">
        <PriorityCard title={`${onboardingCompleteCount}/${onboardingPathSteps.length} onboarding steps`}>
          <div className="teacher-detail-checklist">
            {onboardingPathSteps.map((step, index) => {
              const item = onboardingItemFor(onboardingTaskId(step));
              const done = Boolean(item?.done);
              const pending = Boolean(item?.pending);
              return (
                <div key={step.id} className="teacher-detail-check">
                  <span className={done ? 'is-done' : pending ? 'is-pending' : 'is-missing'}>{done ? 'Done' : pending ? 'Pending' : `${index + 1}`}</span>
                  <span>
                    {step.title}
                    {item?.detail && <small className="teacher-detail-check-detail">{item.detail}</small>}
                  </span>
                  <button type="button" className="teacher-detail-inline-action" onClick={() => openTab('compliance')}>{done ? 'View' : pending ? 'Review' : 'Resolve'}</button>
                </div>
              );
            })}
          </div>
        </PriorityCard>
        <PriorityCard title="Certification progress">
          {certificationViews.map(view => (
            <button key={view.certification.id} type="button" className="teacher-detail-overview-cert" onClick={() => openTab('compliance', 'teacher-certifications')}>
              <strong>{view.certification.title}</strong>
              <span>{view.complete}/{view.total}</span>
              <i><b style={{ width: `${view.total ? view.complete / view.total * 100 : 0}%` }} /></i>
            </button>
          ))}
        </PriorityCard>
        <PriorityCard title="Upcoming Classes">
          {upcomingEvents.length === 0 ? (
            <p className="teacher-detail-muted">No upcoming assigned classes.</p>
          ) : (
            <div className="teacher-detail-event-list">
              {upcomingEvents.map(ev => <EventRow key={ev.id} event={ev} />)}
            </div>
          )}
        </PriorityCard>
        <PriorityCard title="Admin activity">
          <Info label="Last login" value={formatDateTime((teacher as any).lastLogin)} />
          <Info label="Login count" value={String((teacher as any).loginCount || 0)} />
        </PriorityCard>
      </section>}

      {activeTab === 'profile' && <section className="teacher-detail-grid teacher-detail-tab-panel" id="teacher-profile">
        <Card title="Status & Publishing">
          <div className="teacher-detail-two-col">
            <EditableSelect
              label="Staff status"
              value={teacher.staffStatus || 'active'}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'sub', label: 'Sub' },
                { value: 'inactive', label: 'Inactive' },
                { value: 'applicant', label: 'Applicant' },
                { value: 'offboarded', label: 'Offboarded' },
              ]}
              onSave={(value) => saveUserPatch({ staffStatus: value as User['staffStatus'] }, 'staff status')}
            />
            <EditableSelect
              label="Public profile"
              value={teacher.isPublic ? 'true' : 'false'}
              options={[{ value: 'true', label: 'Public' }, { value: 'false', label: 'Private' }]}
              onSave={(value) => saveUserPatch({ isPublic: value === 'true' }, 'public profile')}
            />
            <EditableSelect
              label="Teacher type"
              value={teacher.teacherType || ''}
              options={[
                { value: '', label: 'Unset' },
                { value: 'lead', label: 'Lead' },
                { value: 'partner', label: 'Partner' },
              ]}
              onSave={(value) => saveUserPatch({ teacherType: (value || undefined) as any }, 'teacher type')}
            />
            <EditableSelect
              label="Admin"
              value={teacher.isAdmin ? 'true' : 'false'}
              options={[{ value: 'true', label: 'Admin' }, { value: 'false', label: 'Not admin' }]}
              onSave={(value) => saveUserPatch({ isAdmin: value === 'true' }, 'admin status')}
            />
          </div>
          <div className="teacher-detail-permissions">
            <div className="teacher-detail-permissions-heading">
              <strong>Admin page permissions</strong>
              <span>{teacher.isAdmin ? 'Full administrators automatically have access to every admin area.' : 'Select only the admin workspaces this teacher should be able to open.'}</span>
            </div>
            {ADMIN_PERMISSIONS.map(permission => {
              const checked = teacher.isAdmin || normalizeAdminPermissions(teacher.adminPermissions).includes(permission.id);
              return <label key={permission.id} className={`teacher-detail-permission ${teacher.isAdmin ? 'is-disabled' : ''}`}>
                <input type="checkbox" checked={checked} disabled={teacher.isAdmin || fieldSaving === 'admin permissions'} onChange={() => void toggleAdminPermission(permission.id)} />
                <span><strong>{permission.label}</strong><small>{permission.description}</small></span>
              </label>;
            })}
          </div>
        </Card>

        <Card title="Profile & Contact">
          {fieldMessage && <div className="teacher-detail-field-message">{fieldMessage}</div>}
          <div className="teacher-detail-two-col">
            <EditableText
              label="Display name"
              value={teacher.displayName || ''}
              saving={fieldSaving === 'display name'}
              onSave={(value) => saveUserPatch({ displayName: value || null }, 'display name')}
            />
            <EditableText
              label="Email"
              type="email"
              value={teacher.email || ''}
              saving={fieldSaving === 'email'}
              onSave={(value) => saveUserPatch({ email: value || null }, 'email')}
            />
            <Info label="Created" value={formatDate((teacher as any).createdAt)} />
            <EditableText
              label="Start date"
              type="date"
              value={teacher.startDate || ''}
              saving={fieldSaving === 'start date'}
              onSave={(value) => saveUserPatch({ startDate: value || undefined }, 'start date')}
            />
            <EditableText
              label="Slack email"
              value={(teacher as any).slackEmail || ''}
              saving={fieldSaving === 'slack email'}
              onSave={(value) => saveUserPatch({ slackEmail: value || null }, 'slack email')}
            />
            <EditableText
              label="LinkedIn"
              value={teacher.linkedinUrl || ''}
              saving={fieldSaving === 'LinkedIn'}
              onSave={(value) => saveUserPatch({ linkedinUrl: value || undefined }, 'LinkedIn')}
            />
            <EditableText
              label="Organization"
              value={String((teacher as any).organization || '')}
              saving={fieldSaving === 'organization'}
              onSave={(value) => saveUserPatch({ organization: value || null } as any, 'organization')}
            />
            <EditablePhone
              label="Phone"
              value={teacher.phoneNumber || ''}
              saving={fieldSaving === 'phone'}
              onSave={(value) => saveUserPatch({ phoneNumber: value || undefined }, 'phone')}
            />
            <EditableText
              label="Timezone"
              value={teacher.timeZone || ''}
              saving={fieldSaving === 'timezone'}
              onSave={(value) => saveUserPatch({ timeZone: value || undefined }, 'timezone')}
            />
            <EditableText
              label="State"
              value={(teacher as any).state || teacher.shippingAddress?.state || ''}
              saving={fieldSaving === 'state'}
              onSave={(value) => saveUserPatch({ state: value || undefined }, 'state')}
            />
            <EditableText
              label="Neighborhood"
              value={teacher.neighborhood || ''}
              saving={fieldSaving === 'neighborhood'}
              onSave={(value) => saveUserPatch({ neighborhood: value || undefined }, 'neighborhood')}
            />
            <Info label="Shipping address" value={formatAddress(teacher.shippingAddress)} wide />
            <Info label="Emergency contact" value={formatEmergencyContact(teacher.emergencyContact)} wide />
          </div>
        </Card>

        <Card title="Teaching Profile">
          <div className="teacher-detail-two-col">
            <EditableText
              label="Subjects"
              value={(teacher.subjects || []).join(', ')}
              help="Comma-separated, e.g. music, ai"
              saving={fieldSaving === 'subjects'}
              onSave={(value) => {
                const allowed = new Set(['music', 'ai']);
                const subjects = Array.from(new Set(value.split(',').map(s => s.trim().toLowerCase()).filter(s => allowed.has(s))));
                return saveUserPatch({ subjects }, 'subjects');
              }}
            />
            <EditableSelect
              label="Music private lessons"
              value={teacher.musicPrivateLessons ? 'yes' : 'no'}
              options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]}
              onSave={(value) => saveUserPatch({ musicPrivateLessons: value === 'yes' }, 'private lessons')}
            />
            <EditableText
              label="Music topics"
              value={(teacher.musicTopics || []).join(', ')}
              help="Comma-separated"
              saving={fieldSaving === 'music topics'}
              onSave={(value) => saveUserPatch({ musicTopics: value.split(',').map(s => s.trim()).filter(Boolean) }, 'music topics')}
              wide
            />
            <EditableTextarea
              label="Public bio"
              value={teacher.bio || ''}
              saving={fieldSaving === 'bio'}
              onSave={(value) => saveUserPatch({ bio: value || null }, 'bio')}
              wide
            />
          </div>
        </Card>
        <Card title="Logistics">
          <div className="teacher-detail-two-col">
            <EditableSelect label="Has car" value={teacher.hasCar === true ? 'true' : teacher.hasCar === false ? 'false' : ''} options={[{ value: '', label: 'Unset' }, { value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]} onSave={(value) => saveUserPatch({ hasCar: value ? value === 'true' : undefined }, 'has car')} />
            <EditableSelect label="Driver license" value={teacher.hasDriversLicense === true ? 'true' : teacher.hasDriversLicense === false ? 'false' : ''} options={[{ value: '', label: 'Unset' }, { value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]} onSave={(value) => saveUserPatch({ hasDriversLicense: value ? value === 'true' : undefined }, 'driver license')} />
            <EditableText label="Devices" type="number" value={teacher.deviceCount === undefined ? '' : String(teacher.deviceCount)} saving={fieldSaving === 'devices'} onSave={(value) => saveUserPatch({ deviceCount: Number(value) || 0 }, 'devices')} />
            <EditableText label="Hourly rate" type="number" value={teacher.hourlyRate === undefined ? '' : String(teacher.hourlyRate)} saving={fieldSaving === 'hourly rate'} onSave={(value) => saveUserPatch({ hourlyRate: Number(value) || 0 }, 'hourly rate')} />
            <EditableText
              label="Gusto employee ID"
              value={teacher.gustoEmployeeId || ''}
              saving={fieldSaving === 'Gusto employee ID'}
              onSave={(value) => saveUserPatch({ gustoEmployeeId: value || null }, 'Gusto employee ID')}
            />
          </div>
        </Card>
      </section>}

      {activeTab === 'compliance' && <section className="teacher-detail-compliance-tab teacher-detail-tab-panel" id="teacher-compliance">
        {fieldMessage && <div className="teacher-detail-field-message">{fieldMessage}</div>}

        <details className="teacher-path-accordion" open>
          <summary>
            <div>
              <small>Same order the teacher sees</small>
              <h2>{onboardingCertification?.title || 'Teacher onboarding'}</h2>
              <p>{onboardingCertification?.description || 'Employment, account, compliance, and payroll setup.'}</p>
            </div>
            <strong>{onboardingCompleteCount} of {onboardingPathSteps.length} complete</strong>
          </summary>
          <ol className="teacher-path-steps">
            {onboardingPathSteps.map((step, index) => {
              const taskId = onboardingTaskId(step);
              const item = onboardingItemFor(taskId);
              const done = Boolean(item?.done);
              const pending = Boolean(item?.pending);
              const optional = step.required === false;
              return (
                <li key={step.id} className={`teacher-path-step ${done ? 'is-complete' : ''} ${pending ? 'is-pending' : ''}`}>
                  <div className="teacher-path-step__marker">{done ? '✓' : index + 1}</div>
                  <div className="teacher-path-step__body">
                    <div className="teacher-path-step__title">
                      <div>
                        <h3>{step.title}{optional ? <em>Optional</em> : null}</h3>
                        {step.description && <p>{step.description}</p>}
                        {item?.detail && <small className="teacher-detail-check-detail">{item.detail}</small>}
                      </div>
                      <b className={done ? 'is-completed' : pending ? 'is-pending_review' : 'is-not_started'}>{done ? 'Complete' : pending ? 'Awaiting review' : optional ? 'As needed' : 'Not started'}</b>
                    </div>
                    {(step.writtenContent || step.video || step.linkUrl || step.documents?.length) && (
                      <div className="teacher-path-step__materials">
                        {step.writtenContent && <p>{step.writtenContent}</p>}
                        {step.video?.url && <a href={step.video.url} target="_blank" rel="noreferrer">Watch training video ↗</a>}
                        {step.linkUrl && <a href={step.linkUrl} target="_blank" rel="noreferrer">{step.linkLabel || 'Open resource'} ↗</a>}
                        {(step.documents || []).map(file => <a key={file.storagePath} href={file.url} target="_blank" rel="noreferrer">{file.name} ↗</a>)}
                      </div>
                    )}
                    <div className="teacher-path-step__actions">
                      {item?.downloadUrl && <a className="teacher-detail-inline-action" href={item.downloadUrl} target="_blank" rel="noreferrer">{item.downloadLabel || 'Download'}</a>}
                      {taskId === 'schoolBackgroundCheck' && (
                        <button type="button" className="teacher-detail-inline-action" disabled={backgroundSubmissionLoading} onClick={() => void openCoriSubmission()}>
                          {backgroundSubmissionLoading ? 'Loading form…' : 'View submitted CORI form'}
                        </button>
                      )}
                      {taskId === 'mandatedReporter' && (
                        <label className="teacher-detail-inline-action">Upload certificate<input type="file" accept="application/pdf,image/*" onChange={event => { const file = event.target.files?.[0]; if (file) void uploadComplianceFile('mandatoryReportingCertificationFile', file); event.target.value = ''; }} /></label>
                      )}
                      <label className="teacher-detail-admin-check">
                        <input type="checkbox" checked={done} disabled={savingOnboardingTask === taskId} onChange={() => void toggleOnboardingTask(taskId, done)} />
                        <span>{savingOnboardingTask === taskId ? 'Saving…' : done ? 'Completed' : pending ? 'Approve review' : 'Mark complete'}</span>
                      </label>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </details>

        <div id="teacher-certifications" className="teacher-detail-anchor-card">
          {certificationViews.map(view => (
            <details className="teacher-path-accordion" open key={view.certification.id}>
              <summary>
                <div>
                  <small>Same path the teacher sees</small>
                  <h2>{view.certification.title}</h2>
                  <p>{view.certification.description}</p>
                </div>
                <strong>{view.complete} of {view.total} complete</strong>
              </summary>
              <div className="teacher-detail-certification-bar"><i style={{ width: `${view.total ? view.complete / view.total * 100 : 0}%` }} /></div>
              <ol className="teacher-path-steps">
                {view.rows.map((row, index) => {
                  if (row.kind === 'module') {
                    return (
                      <li key={`module:${row.module.id}`} className={`teacher-path-step ${row.done ? 'is-complete' : ''}`}>
                        <div className="teacher-path-step__marker">{row.done ? '✓' : index + 1}</div>
                        <details className="teacher-path-module" open={!row.done}>
                          <summary>
                            <div>
                              <small>Module</small>
                              <h3>{row.module.title}</h3>
                              {row.module.description && <p>{row.module.description}</p>}
                            </div>
                            <b>{row.complete}/{row.total} lessons</b>
                          </summary>
                          <div className="teacher-path-lessons">
                            {row.lessons.map(lesson => {
                              const key = `${view.certification.id}_${lesson.stepId}`;
                              return (
                                <div key={lesson.training.id} className={`teacher-path-lesson ${lesson.done ? 'is-complete' : ''}`}>
                                  <div>
                                    <small>{formatTrainingDuration(lesson.training)} · {lesson.done ? 'Complete' : lesson.pending ? 'Awaiting review' : 'Required lesson'}</small>
                                    <h4>{lesson.training.title}</h4>
                                    {lesson.training.description && <p>{lesson.training.description}</p>}
                                  </div>
                                  <button type="button" className="teacher-detail-inline-action" disabled={savingLearningStep === key} onClick={() => void saveLearningStep(view.certification.id, lesson.stepId, lesson.done ? 'not_started' : 'completed')}>
                                    {savingLearningStep === key ? 'Saving…' : lesson.done ? 'Mark incomplete' : 'Mark complete'}
                                  </button>
                                </div>
                              );
                            })}
                            {!row.lessons.length && <p className="teacher-detail-muted">No published lessons in this module yet.</p>}
                          </div>
                        </details>
                      </li>
                    );
                  }
                  if (row.kind === 'training') {
                    const key = `${view.certification.id}_${row.stepId}`;
                    return (
                      <li key={`training:${row.training.id}`} className={`teacher-path-step ${row.done ? 'is-complete' : ''}`}>
                        <div className="teacher-path-step__marker">{row.done ? '✓' : index + 1}</div>
                        <div className="teacher-path-step__body">
                          <div className="teacher-path-step__title">
                            <div>
                              <small>Lesson</small>
                              <h3>{row.training.title}</h3>
                              {row.training.description && <p>{row.training.description}</p>}
                            </div>
                            <b className={row.done ? 'is-completed' : row.pending ? 'is-pending_review' : 'is-not_started'}>{learningStatusLabel(row.entry?.status)}</b>
                          </div>
                          <div className="teacher-path-step__actions">
                            <small>{formatTrainingDuration(row.training)}</small>
                            <button type="button" className="teacher-detail-inline-action" disabled={savingLearningStep === key} onClick={() => void saveLearningStep(view.certification.id, row.stepId, row.done ? 'not_started' : 'completed')}>
                              {savingLearningStep === key ? 'Saving…' : row.done ? 'Mark incomplete' : 'Mark complete'}
                            </button>
                          </div>
                        </div>
                      </li>
                    );
                  }
                  const key = `${view.certification.id}_${row.step.id}`;
                  const questions = getQuizQuestions(row.step);
                  const answers = row.entry?.quizAnswers || {};
                  return (
                    <li key={`step:${row.step.id}`} className={`teacher-path-step ${row.done ? 'is-complete' : ''} ${row.pending ? 'is-pending' : ''}`}>
                      <div className="teacher-path-step__marker">{row.done ? '✓' : index + 1}</div>
                      <div className="teacher-path-step__body">
                        <div className="teacher-path-step__title">
                          <div>
                            <small>{COMPLETION_TYPE_LABELS[row.step.completionType]}</small>
                            <h3>{row.step.title}</h3>
                            {row.step.description && <p>{row.step.description}</p>}
                          </div>
                          <b className={row.done ? 'is-completed' : row.pending ? 'is-pending_review' : 'is-not_started'}>{learningStatusLabel(row.entry?.status)}</b>
                        </div>
                        {(row.step.writtenContent || row.step.video || row.step.linkUrl || row.step.documents?.length) && (
                          <div className="teacher-path-step__materials">
                            {row.step.writtenContent && <p>{row.step.writtenContent}</p>}
                            {row.step.video?.url && <a href={row.step.video.url} target="_blank" rel="noreferrer">Watch training video ↗</a>}
                            {row.step.linkUrl && <a href={row.step.linkUrl} target="_blank" rel="noreferrer">{row.step.linkLabel || 'Open resource'} ↗</a>}
                            {(row.step.documents || []).map(file => <a key={file.storagePath} href={file.url} target="_blank" rel="noreferrer">{file.name} ↗</a>)}
                          </div>
                        )}
                        {row.entry?.evidence && <a className="teacher-detail-inline-action" href={row.entry.evidence.url} target="_blank" rel="noreferrer">Review {row.entry.evidence.name}</a>}
                        {questions.some(question => answers[question.id]?.trim()) && (
                          <div className="teacher-path-quiz">
                            {questions.map((question, questionIndex) => (
                              <div key={question.id}>
                                <strong>{questions.length > 1 ? `Question ${questionIndex + 1}` : 'Submitted answer'}</strong>
                                {questions.length > 1 && <p>{question.prompt}</p>}
                                <p>{answers[question.id] || 'No answer'}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        {!questions.some(question => answers[question.id]?.trim()) && row.entry?.quizAnswer && (
                          <div className="teacher-path-quiz"><strong>Submitted answer</strong><p>{row.entry.quizAnswer}</p></div>
                        )}
                        <div className="teacher-path-step__actions">
                          {row.pending && <button type="button" className="teacher-detail-inline-action" disabled={savingLearningStep === key} onClick={() => void saveLearningStep(view.certification.id, row.step.id, 'completed')}>Approve</button>}
                          {row.pending && <button type="button" className="teacher-detail-inline-action" disabled={savingLearningStep === key} onClick={() => void saveLearningStep(view.certification.id, row.step.id, 'rejected')}>Reject</button>}
                          <button type="button" className="teacher-detail-inline-action" disabled={savingLearningStep === key} onClick={() => void saveLearningStep(view.certification.id, row.step.id, row.done ? 'not_started' : 'completed')}>
                            {savingLearningStep === key ? 'Saving…' : row.done ? 'Mark incomplete' : 'Mark complete'}
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </details>
          ))}
          {!certificationViews.length && <Card title="Teaching Certification"><p className="teacher-detail-muted">No certifications assigned.</p></Card>}
        </div>

        {extraComplianceItems.length > 0 && (
          <Card title="Additional compliance">
            <div className="teacher-detail-checklist">
              {extraComplianceItems.map(item => (
                <div key={item.id} className="teacher-detail-check">
                  <span className={item.done ? 'is-done' : 'is-missing'}>{item.done ? 'Done' : 'Missing'}</span>
                  <span>{item.label}</span>
                  <div className="teacher-detail-check-actions">
                    {item.downloadUrl && <a className="teacher-detail-inline-action" href={item.downloadUrl} target="_blank" rel="noreferrer">{item.downloadLabel || 'Download'}</a>}
                    {!item.done && item.action === 'upload' && <label className="teacher-detail-inline-action">Upload<input type="file" accept="application/pdf,image/*" onChange={e => { const file = e.target.files?.[0]; if (file) void uploadComplianceFile(item.id as any, file); e.target.value = ''; }} /></label>}
                    {item.action === 'toggle' && <button type="button" className="teacher-detail-inline-action" onClick={() => void saveCompliancePatch({ cycCleared: !item.done }, 'CYC clearance')}>{item.done ? 'Uncheck' : 'Mark cleared'}</button>}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        <div id="teacher-partner-clearances" className="teacher-detail-anchor-card"><Card title="Partner Clearances">
          {schoolClearances.length === 0 ? <p className="teacher-detail-muted">No partner clearances yet.</p> : <div className="teacher-detail-clearance-list">{schoolClearances.map(item => <div key={item.id} className="teacher-detail-clearance"><div><strong>{item.typeLabel || item.type}{(item as any).historical ? ' (historical)' : ''}</strong><span>{uploadedAt(item.file || (item as any))}</span></div>{item.file?.url ? <a href={item.file.url} target="_blank" rel="noreferrer">Open</a> : <span>No file</span>}</div>)}</div>}
        </Card></div>
      </section>}

      {activeTab === 'availability' && <section className="teacher-detail-availability teacher-detail-tab-panel" id="teacher-availability">
        <div className="teacher-detail-section-head">
          <div>
            <h2>Season Availability</h2>
            <p>Admins can manually update this teacher's weekly template for each season.</p>
          </div>
          {dirty[selectedQuarter] && <span className="teacher-detail-unsaved">Unsaved changes</span>}
        </div>

        <div className="teacher-detail-quarter-tabs">
          {TEACHER_AVAILABILITY_QUARTERS.map(q => (
            <button
              key={q.key}
              type="button"
              className={selectedQuarter === q.key ? 'is-active' : ''}
              onClick={() => {
                setSelectedQuarter(q.key);
                setSaveError(null);
              }}
            >
              {q.label}
            </button>
          ))}
        </div>

        <div className="teacher-detail-teacher-week-grid">
          {TEACHER_AVAILABILITY_DAYS.map(day => {
            const dayTemplate = (selectedWeek as any)[day.key] as AvailabilityDayTemplate;
            const block = firstAvailabilityBlock(dayTemplate, day.key);
            const dayStatus = dayTemplate.status || block.status;
            const dayStatusClass = dayStatus === 'available'
              ? 'is-available'
              : dayStatus === 'maybe'
                ? 'is-maybe'
                : 'is-unavailable';
            return (
              <div key={day.key} className={`teacher-detail-teacher-day ${dayStatusClass}`}>
                <div className="teacher-detail-day-head">
                  <div className="teacher-detail-day-name">{day.label}</div>
                  <div className="teacher-detail-day-time-row" aria-label="Change time details">
                    <input
                      type="time"
                      className="teacher-detail-day-time-input"
                      value={block.start}
                      onChange={(e) => updateDraftDay(day.key, { start: e.target.value })}
                      title="Start time"
                    />
                    <span className="teacher-detail-day-time-sep">-</span>
                    <input
                      type="time"
                      className="teacher-detail-day-time-input"
                      value={block.end}
                      onChange={(e) => updateDraftDay(day.key, { end: e.target.value })}
                      title="End time"
                    />
                  </div>
                </div>

                <div className="teacher-detail-teacher-status">
                  <button
                    type="button"
                    className={`teacher-detail-ta-pill ${dayStatus === 'available' ? 'is-on' : ''}`}
                    onClick={() => updateDraftDay(day.key, { status: 'available' })}
                  >
                    Available
                  </button>
                  <button
                    type="button"
                    className={`teacher-detail-ta-pill ${dayStatus === 'maybe' ? 'is-on is-maybe' : ''}`}
                    onClick={() => updateDraftDay(day.key, { status: 'maybe' })}
                  >
                    Maybe
                  </button>
                  <button
                    type="button"
                    className={`teacher-detail-ta-pill ${dayStatus === 'unavailable' ? 'is-on is-off' : ''}`}
                    onClick={() => updateDraftDay(day.key, { status: 'unavailable' })}
                  >
                    Not available
                  </button>
                </div>

                {dayStatus === 'maybe' && (
                  <div className="teacher-detail-maybe">
                    <label className="teacher-detail-edit-label">
                      Reason (required)
                      <select
                        value={dayTemplate.maybeReason || ''}
                        onChange={(e) => updateDraftDay(day.key, { maybeReason: (e.target.value || null) as AvailabilityMaybeReason | null })}
                      >
                        <option value="">Select...</option>
                        {TEACHER_AVAILABILITY_MAYBE_REASONS.map(reason => (
                          <option key={reason.value} value={reason.value}>{reason.label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="teacher-detail-edit-label">
                      Note (optional)
                      <input
                        type="text"
                        value={dayTemplate.maybeNote || ''}
                        onChange={(e) => updateDraftDay(day.key, { maybeNote: e.target.value })}
                        placeholder="Any extra context..."
                      />
                    </label>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {saveError && <div className="teacher-detail-error teacher-detail-error--inline">{saveError}</div>}
        <div className="teacher-detail-save-row">
          <div className="teacher-detail-muted">
            Last saved: {formatDateTime((teacher.availabilityTemplates as any)?.[selectedQuarter]?.updatedAt)}
          </div>
          <button
            type="button"
            className="teacher-detail-primary"
            onClick={() => void saveSelectedQuarter()}
            disabled={savingQuarter === selectedQuarter}
          >
            {savingQuarter === selectedQuarter ? 'Saving...' : `Save ${TEACHER_AVAILABILITY_QUARTERS.find(q => q.key === selectedQuarter)?.label}`}
          </button>
        </div>

        <div className="teacher-detail-availability-bottom">
          <Card title="Season Exceptions">
            <div className="teacher-detail-exc-legend">
              <span><span className="teacher-detail-legend-dot is-available" /> Available</span>
              <span><span className="teacher-detail-legend-dot is-maybe" /> Maybe</span>
              <span><span className="teacher-detail-legend-dot is-unavailable" /> Not available</span>
              <span><span className="teacher-detail-legend-dot is-class" /> Scheduled class</span>
            </div>
            <p className="teacher-detail-muted">Click a day to add or edit a date-specific exception for this teacher.</p>
            <div className="teacher-detail-calendar-wrap">
              <FullCalendar
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                initialDate={TEACHER_AVAILABILITY_QUARTERS.find(q => q.key === selectedQuarter)?.startDate}
                key={selectedQuarter}
                headerToolbar={{ left: 'prev,next today', center: 'title', right: '' }}
                height="auto"
                fixedWeekCount={false}
                showNonCurrentDates={false}
                dayMaxEventRows={3}
                events={events
                  .filter(ev => quarterContainsDate(selectedQuarter, ymdFromDate(ev.start)))
                  .map(ev => ({
                    id: ev.id,
                    title: ev.title,
                    start: ev.start,
                    end: ev.end,
                    extendedProps: { kind: 'class' },
                  }))}
                dateClick={(arg) => openExceptionEditor(String((arg as any).dateStr || ymdFromDate(arg.date)))}
                eventClick={(info) => {
                  info.jsEvent?.preventDefault?.();
                  info.jsEvent?.stopPropagation?.();
                  if (info.event.start) openExceptionEditor(ymdFromDate(info.event.start));
                }}
                dayCellClassNames={(arg) => {
                  const dk = String((arg as any).dateStr || ymdFromDate(arg.date));
                  const classes: string[] = [];
                  const st = statusForDateKey(dk);
                  if (st) classes.push(`teacher-detail-cal-${st}`);
                  const hasException = quarterExceptions.some(ex => ex.dateKey === dk);
                  if (hasException) {
                    const ex = quarterExceptions.find(x => x.dateKey === dk)?.value;
                    const exSt = ex?.status || (ex?.type === 'unavailable' ? 'unavailable' : 'available');
                    classes.push('teacher-detail-has-exception', `teacher-detail-exc-dot-${exSt}`);
                  }
                  return classes;
                }}
                eventDidMount={(info) => {
                  try {
                    (info.el as HTMLElement).classList.add('teacher-detail-class-event');
                  } catch (_) {}
                }}
              />
            </div>
          </Card>

          <div id="teacher-classes" className="teacher-detail-anchor-card"><Card title="Assigned Classes This Season">
            {currentQuarterEvents.length === 0 ? (
              <p className="teacher-detail-muted">No assigned classes in this season.</p>
            ) : (
              <div className="teacher-detail-event-list">
                {currentQuarterEvents.slice(0, 12).map(ev => <EventRow key={ev.id} event={ev} />)}
              </div>
            )}
          </Card></div>
        </div>

        {selectedExceptionDate && exceptionDraft && (
          <div className="teacher-detail-modal-overlay" onClick={closeExceptionEditor}>
            <div className="teacher-detail-modal" onClick={(e) => e.stopPropagation()}>
              <div className="teacher-detail-modal-head">
                <div>
                  <h2>{selectedExceptionDate}</h2>
                  <p>Override weekly availability for this specific date.</p>
                </div>
                <button type="button" className="teacher-detail-refresh" onClick={closeExceptionEditor}>Close</button>
              </div>

              <div className="teacher-detail-teacher-status">
                {(['available', 'maybe', 'unavailable'] as AvailabilityStatus[]).map(status => (
                  <button
                    key={status}
                    type="button"
                    className={`teacher-detail-ta-pill ${exceptionDraft.status === status ? status === 'maybe' ? 'is-on is-maybe' : status === 'unavailable' ? 'is-on is-off' : 'is-on' : ''}`}
                    onClick={() => setExceptionDraft(prev => prev ? ({ ...prev, status }) : prev)}
                  >
                    {statusLabel(status)}
                  </button>
                ))}
              </div>

              <div className="teacher-detail-modal-grid">
                <label className="teacher-detail-edit-label">
                  Start
                  <input
                    type="time"
                    value={exceptionDraft.start}
                    onChange={(e) => setExceptionDraft(prev => prev ? ({ ...prev, start: e.target.value }) : prev)}
                  />
                </label>
                <label className="teacher-detail-edit-label">
                  End
                  <input
                    type="time"
                    value={exceptionDraft.end}
                    onChange={(e) => setExceptionDraft(prev => prev ? ({ ...prev, end: e.target.value }) : prev)}
                  />
                </label>
              </div>

              {exceptionDraft.status === 'maybe' && (
                <div className="teacher-detail-modal-grid">
                  <label className="teacher-detail-edit-label">
                    Reason (required)
                    <select
                      value={exceptionDraft.maybeReason || ''}
                      onChange={(e) => setExceptionDraft(prev => prev ? ({ ...prev, maybeReason: (e.target.value || null) as AvailabilityMaybeReason | null }) : prev)}
                    >
                      <option value="">Select...</option>
                      {TEACHER_AVAILABILITY_MAYBE_REASONS.map(reason => (
                        <option key={reason.value} value={reason.value}>{reason.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="teacher-detail-edit-label">
                    Note (optional)
                    <input
                      type="text"
                      value={exceptionDraft.maybeNote}
                      onChange={(e) => setExceptionDraft(prev => prev ? ({ ...prev, maybeNote: e.target.value }) : prev)}
                    />
                  </label>
                </div>
              )}

              {exceptionError && <div className="teacher-detail-error teacher-detail-error--inline">{exceptionError}</div>}
              <div className="teacher-detail-modal-actions">
                <button type="button" className="teacher-detail-refresh" onClick={() => void deleteException()} disabled={exceptionSaving}>
                  Delete exception
                </button>
                <button type="button" className="teacher-detail-primary" onClick={() => void saveException()} disabled={exceptionSaving}>
                  {exceptionSaving ? 'Saving...' : 'Save exception'}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>}

      {(backgroundSubmission || backgroundSubmissionError) && (
        <div className="teacher-detail-modal-overlay" onClick={() => { setBackgroundSubmission(null); setBackgroundSubmissionError(null); }}>
          <div className="teacher-detail-modal teacher-detail-cori-modal" role="dialog" aria-modal="true" aria-label="Submitted CORI authorization form" onClick={(event) => event.stopPropagation()}>
            <div className="teacher-detail-modal-head">
              <div><h2>Submitted CORI form</h2><p>{backgroundSubmission?.submittedAt ? `Submitted ${formatDateTime(backgroundSubmission.submittedAt)}` : 'Massachusetts CORI authorization'}</p></div>
              <button type="button" className="teacher-detail-refresh" onClick={() => { setBackgroundSubmission(null); setBackgroundSubmissionError(null); }}>Close</button>
            </div>
            <div className="teacher-detail-sensitive-notice">Sensitive personal information. Use only to submit the authorized CORI application.</div>
            {backgroundSubmissionError && <div className="teacher-detail-error teacher-detail-error--inline">{backgroundSubmissionError}</div>}
            {backgroundSubmission && <div className="teacher-detail-cori-grid">
              {CORI_FORM_FIELDS.map(field => {
                const raw = backgroundSubmission.form[field.key];
                const value = field.key === 'lastSixSsn' && backgroundSubmission.form.noSocialSecurityNumber
                  ? 'No Social Security number'
                  : Array.isArray(raw) ? raw.join(', ') : String(raw ?? '').trim() || '—';
                return <Info key={field.key} label={field.label} value={value} />;
              })}
              <Info label="Authorization" value={backgroundSubmission.form.attested === true ? 'Authorized and attested' : 'Not recorded'} />
            </div>}
          </div>
        </div>
      )}
    </div>
  );
};

const Info: React.FC<{ label: string; value: string; href?: string | null; wide?: boolean; preserve?: boolean }> = ({ label, value, href, wide, preserve }) => (
  <div className={`teacher-detail-info ${wide ? 'is-wide' : ''}`}>
    <span>{label}</span>
    {href ? (
      <a href={href} target="_blank" rel="noreferrer">{value}</a>
    ) : (
      <strong style={preserve ? { whiteSpace: 'pre-wrap' } : undefined}>{value}</strong>
    )}
  </div>
);

const EditableText: React.FC<{
  label: string;
  value: string;
  type?: string;
  help?: string;
  wide?: boolean;
  saving?: boolean;
  onSave: (value: string) => void | Promise<void>;
}> = ({ label, value, type = 'text', help, wide, saving, onSave }) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <label className={`teacher-detail-edit-label ${wide ? 'is-wide' : ''}`}>
      <span>{label}</span>
      <input
        type={type}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft !== value) void onSave(draft);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && draft !== value) {
            e.currentTarget.blur();
          }
        }}
      />
      {help && <small>{help}</small>}
      {saving && <small>Saving...</small>}
    </label>
  );
};

const EditablePhone: React.FC<{
  label: string;
  value: string;
  saving?: boolean;
  onSave: (value: string) => void | Promise<void>;
}> = ({ label, value, saving, onSave }) => {
  const [draft, setDraft] = useState(formatUsPhoneDisplay(value));
  useEffect(() => setDraft(formatUsPhoneDisplay(value)), [value]);

  const commit = () => {
    const normalized = normalizeUsPhoneForSave(draft);
    if (normalized === null) {
      void window.appAlert('Phone number must have exactly 10 digits including area code.');
      setDraft(formatUsPhoneDisplay(value));
      return;
    }
    if (normalized !== String(value || '')) void onSave(normalized);
    else setDraft(formatUsPhoneDisplay(value));
  };

  return (
    <label className="teacher-detail-edit-label">
      <span>{label}</span>
      <input
        type="tel"
        value={draft}
        placeholder="(555) 123-4567"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
      />
      {saving && <small>Saving...</small>}
    </label>
  );
};

const EditableTextarea: React.FC<{
  label: string;
  value: string;
  wide?: boolean;
  saving?: boolean;
  onSave: (value: string) => void | Promise<void>;
}> = ({ label, value, wide, saving, onSave }) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <label className={`teacher-detail-edit-label ${wide ? 'is-wide' : ''}`}>
      <span>{label}</span>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft !== value) void onSave(draft);
        }}
      />
      {saving && <small>Saving...</small>}
    </label>
  );
};

const EditableSelect: React.FC<{
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onSave: (value: string) => void | Promise<void>;
}> = ({ label, value, options, onSave }) => (
  <label className="teacher-detail-edit-label">
    <span>{label}</span>
    <select value={value} onChange={(e) => void onSave(e.target.value)}>
      {options.map(option => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
  </label>
);

const Card: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="teacher-detail-card">
    <h2>{title}</h2>
    {children}
  </section>
);

const PriorityCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="teacher-detail-priority-card">
    <h2>{title}</h2>
    {children}
  </section>
);

const EventRow: React.FC<{ event: TeacherEvent }> = ({ event }) => (
  <div className="teacher-detail-event">
    <div>
      <strong>{event.title}</strong>
      <span>{event.role === 'lead' ? 'Lead teacher' : 'Supporting teacher'}</span>
    </div>
    <div>
      <strong>{formatDate(event.start, { weekday: 'short', month: 'short', day: 'numeric' })}</strong>
      <span>{event.start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}-{event.end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
    </div>
  </div>
);

export default TeacherAdminDetailPage;
